import { useEffect, useState } from 'react';
import { paragon, SDK_EVENT, type ConnectUser } from '@useparagon/connect';

import { getParagonToken } from '@/services/workflowApi';

const PARAGON_PROJECT_ID = import.meta.env.VITE_PARAGON_PROJECT_ID as string;

function useParagonAuth() {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<ConnectUser | undefined>();
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await getParagonToken();
        setToken(response.token);
      } catch (e) {
        setError(e as Error);
      }
    };
    void fetchToken();
  }, []);

  // Keep user state in sync when integrations are installed/uninstalled via the modal.
  useEffect(() => {
    const listener = () => {
      const authedUser = paragon.getUser();
      if (authedUser.authenticated) {
        setUser({ ...authedUser });
      }
    };
    paragon.subscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, listener);
    paragon.subscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, listener);

    return () => {
      paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, listener);
      paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, listener);
    };
  }, []);

  // WHY: Separate effect so auth only runs once we have a token. Uses exponential
  // backoff to handle transient Paragon SDK initialization delays (mirrors lead-pages pattern).
  // Cleanup cancels any in-flight retry chain if the component unmounts mid-backoff,
  // preventing state updates on an unmounted component and competing retry chains on remount.
  useEffect(() => {
    if (!token || error) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const maxRetries = 5;

    const tryAuth = async (attempt: number, delay: number): Promise<void> => {
      if (cancelled) return;
      try {
        await paragon.authenticate(PARAGON_PROJECT_ID, token);
        if (!cancelled) {
          setUser(paragon.getUser());
          setIsAuthenticated(true);
        }
      } catch (e) {
        if (cancelled) return;
        if (attempt < maxRetries) {
          await new Promise((res) => {
            timeoutId = setTimeout(res, delay);
          });
          await tryAuth(attempt + 1, delay * 2);
        } else {
          setError(e as Error);
        }
      }
    };

    void tryAuth(1, 500);
    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [token, error]);

  return { user, error, isAuthenticated };
}

export default useParagonAuth;
